import { PagesizeType } from '../../event/service/pagination/pagesize-type';
import { Constants } from './../../shared/constants';
import { Injectable } from '@angular/core';
import { LogService } from '../../shared/log/log.service';
import { UserToken } from '../models/user-token.model';
import 'polyfills';
import { DashboardFilterState } from '../../event/dashboard/dashboard-page/dashboard-filter';

@Injectable()
export class UserLocalStorageService {
  private readonly TOKEN_NAME = 'id_token';
  private readonly TOKEN_COMMISSION_NAME = 'commission_token';
  private readonly EMAIL_PREFIX = 'dashboard_filters_';
  private readonly EXP_TOKEN_NAME = 'exp_token';

  constructor(
    private logService: LogService
  ) {
  }

  getToken(): string {
    try {
      return this.fromUserPermissions().token;
    } catch (error) {
      this.removeUserToken();
      this.logService.error('Unable to read token' + error);
      return null;
    }
  }

  getExpToken(): number {
    try {
      let exp = Number(this.getItem(this.EXP_TOKEN_NAME));
      return exp;
    } catch (error) {
      this.logService.error('Unable to read exp token' + error);
      return null;
    }
  }

  getUserPermissionToken(): UserToken {
    try {
      return this.fromUserPermissions();
    } catch (error) {
      this.removeUserToken();
      this.logService.error('Unable to read token' + error);
      return null;
    }
  }

  getTokenCommission(): string {
    try {
      return this.fromUserPermissionsCommission().token;
    } catch (error) {
      this.removeUserToken();
      this.logService.error('Unable to read token' + error);
      return null;
    }
  }

  getPermissions(): string[] {
    try {
      return this.fromUserPermissions().permissions;
    } catch (error) {
      this.removeUserToken();
      this.logService.error('Unable to read token' + error);
      return [];
    }
  }

  getPermissionsCommission(): string[] {
    try {
      return this.fromUserPermissionsCommission().permissions;
    } catch (error) {
      this.removeUserToken();
      this.logService.error('Unable to read token' + error);
      return [];
    }
  }

  getDashboardFilterState(email: string): DashboardFilterState {
    email = this.EMAIL_PREFIX + email;
    const dashboardFilterStateJson = this.getItemAsJson(email);
    return new DashboardFilterState(dashboardFilterStateJson);
  }

  removeUserToken(): void {
    this.removeItem(this.TOKEN_NAME);
  }

  setUserToken(userPermission: UserToken): void {
    this.setItem(this.TOKEN_NAME, JSON.stringify(userPermission));
  }

  setDashboardFilterState(email: string, dashboardFilterState: DashboardFilterState): void {
    email = this.EMAIL_PREFIX + email;
    this.setItem(email, JSON.stringify(dashboardFilterState));
  }

  private fromUserPermissions(): UserToken {
    return new UserToken(this.getItemAsJson(this.TOKEN_NAME));
  }

  private fromUserPermissionsCommission(): UserToken {
    let role: any = this.getItemAsJsonCommission(this.TOKEN_COMMISSION_NAME);
    role.permissions = ['GetFiles', 'AddFiles', 'DeleteFiles'];
    return new UserToken(role);
  }

  setItem(key, value): void {
    localStorage.setItem(key, value);
  }

  getItem(key): string {
    return localStorage.getItem(key);
  }

  getItemAsJson(key): Object {
    try {
      const stringItem = this.getItem(key);
      if (stringItem != null) {
        return JSON.parse(stringItem);
      }
    } catch (error) {
      this.removeItem(key);
      this.logService.error('Unable to read token' + error);
    }

    return null;
  }

  getItemAsJsonCommission(key): Object {
    try {
      const stringItem = this.getItem(key);
      if (stringItem != null) {
        return {token: stringItem, permission: []};
      }
    } catch (error) {
      this.removeItem(key);
      this.logService.error('Unable to read token' + error);
    }

    return null;
  }

  removeItem(key): void {
    localStorage.removeItem(key);
  }

  get length(): number {
    return localStorage.length;
  }

  key(i): string {
    return localStorage.key(i);
  }

  public setToken(token: string) {
    let userPermission = this.getItemAsJson(this.TOKEN_NAME) as UserToken;
    userPermission.token = token;
    this.setUserToken(userPermission);
  }

  public setExpToken(exp: string) {
    this.setItem(this.EXP_TOKEN_NAME, exp);
  }

  resetPagesizes() {
    this.setPagesize(PagesizeType.EVENTS, Constants.SEARCH_DEFAULT_ITEMS_PER_PAGE);
    this.setPagesize(PagesizeType.GROUP_RESERVATIONS_SUMMARY, Constants.SEARCH_DEFAULT_ITEMS_PER_PAGE);
    this.setPagesize(PagesizeType.GROUP_RESERVATIONS_NO_MATCHES, Constants.SEARCH_DEFAULT_ITEMS_PER_PAGE);
    this.setPagesize(PagesizeType.GROUP_RESERVATIONS_PARTIAL_MATCHES, Constants.SEARCH_DEFAULT_ITEMS_PER_PAGE);
    this.setPagesize(PagesizeType.GROUP_RESERVATIONS_EXACT_MATCHES, Constants.SEARCH_DEFAULT_ITEMS_PER_PAGE);
    this.setPagesize(PagesizeType.RSVP_SUMMARY, Constants.SEARCH_DEFAULT_ITEMS_PER_PAGE);
    this.setPagesize(PagesizeType.RSVP_NO_MATCHES, Constants.SEARCH_DEFAULT_ITEMS_PER_PAGE);
    this.setPagesize(PagesizeType.RSVP_PARTIAL_MATCHES, Constants.SEARCH_DEFAULT_ITEMS_PER_PAGE);
    this.setPagesize(PagesizeType.RSVP_EXACT_MATCHES, Constants.SEARCH_DEFAULT_ITEMS_PER_PAGE);
    this.setPagesize(PagesizeType.TRANSIENT_RESERVATIONS, Constants.SEARCH_DEFAULT_ITEMS_PER_PAGE);
    this.setPagesize(PagesizeType.PAYMENT_RESERVATIONS, Constants.SEARCH_DEFAULT_ITEMS_PER_PAGE);
    this.setPagesize(PagesizeType.PAYMENT_RSVP, Constants.SEARCH_DEFAULT_ITEMS_PER_PAGE);
    this.setPagesize(PagesizeType.MATCHING_PROPERTY, Constants.SEARCH_DEFAULT_ITEMS_PER_PAGE);
    this.setPagesize(PagesizeType.EVENT_MATCHING, Constants.SEARCH_DEFAULT_ITEMS_PER_PAGE);
    this.setPagesize(PagesizeType.MATCHING_AGENCY, Constants.SEARCH_DEFAULT_ITEMS_PER_PAGE);
  }

  getPagesize(pagesizeType: PagesizeType): number {
    return +this.getItem(pagesizeType);
  }

  setPagesize(pagesizeType: PagesizeType, pagesize: number) {
    this.setItem(pagesizeType, pagesize);
  }

}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/qfGQY7pgg/1/oneonyx/grouppay-group/grouppay/pioneer-web/src/app/permission/services/user.local.storage.service.ts